/**
 * Created: 2026-04-08
 * Initial commit: Split SlotsPage into SlotsPage/ directory (feat/event/mr-10)
 * Updated: 2026-04-12 — replaced DataGridPro with pure CSS Grid (feat/event/mr-14)
 * Updated: 2026-10-21 — moved styles from SlotGrid.module.css into sx
 * Updated: 2026-10-21 — `currentCoachId`: in the coach calendar, the viewing
 * coach's own appointments show green, as in the mini coach calendar
 * Updated: 2026-10-21 — In the coach calendar, used slots other than
 * appointments (orders, events, reservations, locks) are plain grey cells
 * with no detail
 * Updated: 2026-10-24 — In the coach calendar, reserved slots (reservations
 * and events) show as in the admin view; only bookings and locks are grey
 *
 * Pure CSS Grid slot table. Replaces the previous DataGridPro implementation.
 * Each data cell carries data-hour and data-court-id attributes so that
 * useDragSelection can identify cells without MUI internals.
 */

import { Box, CircularProgress, Tooltip } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { GAME_FORMAT_CHIP } from '@tc-app/shared/labels'
import type { SpanBlock } from '@tc-app/shared/slots/mergeSlots'
import { SlotStatus } from '@tc-app/shared/slots/types'
import { Fragment } from 'react'
import type { SlotCoachingMeta, SlotMeta } from './types'

/**
 * Plain (non-hashed) class names for the grid's cells. Styling lives in one
 * `sx` object on the wrapper (`gridSx`) rather than per-cell `sx`, so the
 * hundreds of cells don't each pay for emotion style resolution, and so
 * useDragSelection can toggle a stable class name directly on the DOM.
 */
const styles = {
  loadingOverlay: 'loadingOverlay',
  grid: 'grid',
  headerCell: 'headerCell',
  timeCell: 'timeCell',
  cell: 'cell',
  unavailable: 'unavailable',
  past: 'past',
  statusReserved: 'statusReserved',
  statusReservedEvent: 'statusReservedEvent',
  statusBooked: 'statusBooked',
  statusLocked: 'statusLocked',
  occupied: 'occupied',
  selected: 'selected',
  span: 'span',
  cellPrice: 'cellPrice',
  cellReason: 'cellReason',
  cellCoaching: 'cellCoaching',
  cellReasonMultiline: 'cellReasonMultiline',
  cellEventInfo: 'cellEventInfo',
} as const

/** Class toggled on cells by useDragSelection while dragging */
export const DRAG_HIGHLIGHT_CLASS = 'dragHighlight'

const BORDER = '1px solid rgba(224, 224, 224, 1)'

const smallLine = {
  fontSize: '0.7rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: 'normal',
  textAlign: 'center',
  width: '100%',
} as const

const gridSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  position: 'relative',
  userSelect: 'none',

  [`& .${styles.loadingOverlay}`]: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
  },

  [`& .${styles.grid}`]: {
    display: 'grid',
    borderTop: BORDER,
    borderLeft: BORDER,
    minWidth: 'max-content',
  },

  // ── header row ──
  [`& .${styles.headerCell}`]: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    background: '#fafafa',
    fontWeight: 600,
    fontSize: '0.8125rem',
    textAlign: 'center',
    padding: '6px 4px',
    borderRight: BORDER,
    borderBottom: '2px solid rgba(224, 224, 224, 1)',
    whiteSpace: 'nowrap',
  },
  // sticky time column header corner
  [`& .${styles.headerCell}:first-of-type`]: {
    left: 0,
    zIndex: 3,
  },

  // ── time label column ──
  [`& .${styles.timeCell}`]: {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    background: '#fafafa',
    fontSize: '0.75rem',
    textAlign: 'center',
    padding: '0 4px',
    borderRight: BORDER,
    borderBottom: BORDER,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 52,
    whiteSpace: 'nowrap',
  },

  // ── data cells ──
  [`& .${styles.cell}`]: {
    borderRight: BORDER,
    borderBottom: BORDER,
    cursor: 'cell',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 52,
    fontSize: '0.8125rem',
    padding: '4px',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  [`& .${styles.cell}.${styles.unavailable}`]: {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    color: 'rgba(0, 0, 0, 0.38)',
    cursor: 'default',
  },
  [`& .${styles.cell}.${styles.past}`]: {
    backgroundColor: '#f5f5f5',
    color: 'rgba(0, 0, 0, 0.38)',
    cursor: 'default',
  },
  [`& .${styles.cell}.${styles.statusReserved}`]: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
  },
  [`& .${styles.cell}.${styles.statusReservedEvent}`]: {
    backgroundColor: '#ede7f6',
    color: '#4527a0',
  },
  [`& .${styles.cell}.${styles.statusBooked}`]: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  [`& .${styles.cell}.${styles.statusLocked}`]: {
    backgroundColor: '#e0e0e0',
    color: '#424242',
  },
  // coach calendar: a used slot other than an appointment, shown without detail
  [`& .${styles.cell}.${styles.occupied}`]: {
    backgroundColor: '#eeeeee',
    color: 'rgba(0, 0, 0, 0.38)',
  },
  [`& .${styles.cell}.${styles.selected}, & .${styles.cell}.${DRAG_HIGHLIGHT_CLASS}`]: {
    backgroundColor: 'rgba(25, 118, 210, 0.16)',
  },
  // merged span cell — height is controlled by grid-row span
  [`& .${styles.cell}.${styles.span}`]: {
    height: 'auto',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 4px',
  },

  // ── cell content ──
  [`& .${styles.cellPrice}`]: {
    textAlign: 'center',
    lineHeight: 'normal',
  },
  [`& .${styles.cellReason}`]: { ...smallLine, color: '#e65100' },
  // coaching appointment slots: label + student name or confirmed/max
  [`& .${styles.cellCoaching}`]: smallLine,
  // reserved (non-event) slots: up to 2 lines of reason text
  [`& .${styles.cellReasonMultiline}`]: {
    fontSize: '0.7rem',
    color: '#e65100',
    whiteSpace: 'pre-line',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: 1.2,
    textAlign: 'center',
    width: '100%',
  },
  [`& .${styles.cellEventInfo}`]: { ...smallLine, color: '#4527a0' },
}

function formatHour(h: number) {
  return `${h.toString().padStart(2, '0')}:00`
}

interface Court {
  id: string
  name: string
  displayOrder: number
}

interface SlotGridProps {
  courts: Court[]
  /** Ordered list of hours to render, derived from the slots query response */
  hours: number[]
  slotLookup: Map<string, SlotMeta>
  /** Cells covered by a span block — rendered by the span layer instead */
  coveredCells: Set<string>
  /** Span blocks to render as merged cells */
  spanBlocks: SpanBlock<SlotMeta>[]
  pastHourThreshold: number
  selectedKeys: Set<string>
  loading: boolean
  gridRef: React.RefObject<HTMLDivElement | null>
  onMouseDown: (e: React.MouseEvent) => void
  onMouseMove: (e: React.MouseEvent) => void
  onMouseUp: (e: React.MouseEvent) => void
  onCellClick: (hour: number, courtId: string) => void
  /**
   * The viewing coach (coach calendar): coaching slots show green when they
   * are the coach's own appointments, instead of when all attendees confirmed.
   * Reserved slots show as in the admin view; bookings and locks show greyed
   * out, with no detail. Only appointments are clickable.
   */
  currentCoachId?: string
}

export default function SlotGrid({
  courts,
  hours,
  slotLookup,
  coveredCells,
  spanBlocks,
  pastHourThreshold,
  selectedKeys,
  loading,
  gridRef,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onCellClick,
  currentCoachId,
}: SlotGridProps) {
  // CSS Grid: 1 time col + 1 col per court, each 100px wide
  const gridTemplateColumns = `80px repeat(${courts.length}, 100px)`

  return (
    <Box
      ref={gridRef}
      sx={gridSx}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <CircularProgress />
        </div>
      )}

      <div className={styles.grid} style={{ gridTemplateColumns }}>
        {/* ── Header row ── */}
        <div className={styles.headerCell}>时段</div>
        {courts.map((c) => (
          <div key={c.id} className={styles.headerCell}>
            {c.name}
          </div>
        ))}

        {/* ── Span blocks (merged cells) ── */}
        {spanBlocks.map((block) => {
          // grid rows: header=1, first hour → row 2, nth hour → row n+2
          const gridRowStart = hours.indexOf(block.hourStart) + 2
          const gridColStart = block.courtIndex + 2 // time col is col 1
          const hidden = isHiddenFromCoach(block.meta, currentCoachId)
          const isClickable =
            !hidden &&
            (currentCoachId != null
              ? !!block.meta.sessionId
              : !!block.meta.eventId ||
                !!block.meta.sessionId ||
                (block.meta.status === SlotStatus.Booked && !!block.meta.orderId))
          const statusClass = hidden
            ? styles.occupied
            : block.meta.coaching
              ? coachingStatusClass(block.meta.coaching, currentCoachId)
              : block.meta.status === SlotStatus.Booked
                ? styles.statusBooked
                : block.meta.status === SlotStatus.Locked
                  ? styles.statusLocked
                  : block.meta.eventId
                    ? styles.statusReservedEvent
                    : styles.statusReserved
          const spanKey = `${block.hourStart}-${courts[block.courtIndex].id}`
          const isSelected = selectedKeys.has(spanKey)
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: grid cell is conditionally interactive
            <div
              key={`span-${block.courtIndex}-${block.hourStart}`}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              className={`${styles.cell} ${styles.span} ${statusClass}${isSelected ? ` ${styles.selected}` : ''}`}
              style={{
                gridRow: `${gridRowStart} / span ${block.rowSpan}`,
                gridColumn: `${gridColStart} / span ${block.colSpan}`,
                height: 'auto',
                cursor: isClickable ? 'pointer' : undefined,
              }}
              data-hour={block.hourStart}
              data-court-id={courts[block.courtIndex].id}
              onClick={() => {
                if (isClickable) {
                  onCellClick(block.hourStart, courts[block.courtIndex].id)
                }
              }}
              onKeyDown={(e) => {
                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                  onCellClick(block.hourStart, courts[block.courtIndex].id)
                }
              }}
            >
              {!hidden && <SlotCellContent meta={block.meta} />}
            </div>
          )
        })}

        {/* ── Data rows ── */}
        {hours.map((hour) => (
          <Fragment key={hour}>
            {/* Time label */}
            <div className={styles.timeCell}>{formatHour(hour)}</div>

            {/* Court cells */}
            {courts.map((court) => {
              const key = `${hour}-${court.id}`
              if (coveredCells.has(key)) return null

              const meta = slotLookup.get(key)
              const hidden = !!meta && isHiddenFromCoach(meta, currentCoachId)
              const isPast = hour < pastHourThreshold
              const isUnavailable = !meta

              const isBookedOrReserved =
                meta?.status === SlotStatus.Booked ||
                meta?.status === SlotStatus.Reserved

              const classNames: string[] = [styles.cell]
              if (hidden) classNames.push(styles.occupied)
              else if (isPast && !isBookedOrReserved) classNames.push(styles.past)
              else if (isUnavailable) classNames.push(styles.unavailable)
              else {
                if (meta.coaching)
                  classNames.push(coachingStatusClass(meta.coaching, currentCoachId))
                else if (meta.status === SlotStatus.Reserved)
                  classNames.push(
                    meta.eventId ? styles.statusReservedEvent : styles.statusReserved,
                  )
                else if (meta.status === SlotStatus.Booked)
                  classNames.push(styles.statusBooked)
                else if (meta.status === SlotStatus.Locked)
                  classNames.push(styles.statusLocked)
              }
              if (selectedKeys.has(key)) classNames.push(styles.selected)

              // In the coach calendar only appointments open on click.
              const isClickable =
                !hidden &&
                (!isPast || isBookedOrReserved) &&
                !!meta &&
                (currentCoachId == null || !!meta.coaching)
              return (
                // biome-ignore lint/a11y/noStaticElementInteractions: grid cell is conditionally interactive
                <div
                  key={key}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  className={classNames.join(' ')}
                  data-hour={hour}
                  data-court-id={court.id}
                  onClick={() => {
                    if (isClickable) onCellClick(hour, court.id)
                  }}
                  onKeyDown={(e) => {
                    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                      onCellClick(hour, court.id)
                    }
                  }}
                >
                  {meta && !hidden && (!isPast || isBookedOrReserved) && (
                    <SlotCellContent meta={meta} />
                  )}
                </div>
              )
            })}
          </Fragment>
        ))}
      </div>
    </Box>
  )
}

/**
 * In the coach calendar, a booked or locked slot: the coach sees only that it
 * is taken. Reserved slots (reservations, events, appointments) show in full.
 */
function isHiddenFromCoach(meta: SlotMeta, currentCoachId?: string) {
  return (
    currentCoachId != null &&
    (meta.status === SlotStatus.Booked || meta.status === SlotStatus.Locked)
  )
}

/**
 * Render the content of a slot cell (price + optional reason/event info).
 * Booked and reserved slots omit the price.
 */
export function SlotCellContent({ meta }: { meta: SlotMeta }) {
  if (meta.coaching) return <CoachingCellContent coaching={meta.coaching} />

  // For event slots, the format label is derived from `event.gameFormat` in
  // the UI rather than read off the slot's `reason` text.
  const gameFormatLabel = meta.event
    ? GAME_FORMAT_CHIP[meta.event.gameFormat].label
    : undefined
  // Reserved (non-event) slots show up to 2 lines of the reason text, since
  // there's no price taking up space in that cell; other statuses get 1 line.
  const reasonLine =
    gameFormatLabel ??
    (meta.status === SlotStatus.Reserved && !meta.event
      ? meta.reason?.split('\n').slice(0, 2).join('\n')
      : meta.reason?.split('\n')[0])
  const ratingRange =
    meta.event?.minRating != null && meta.event?.maxRating != null
      ? `${meta.event.minRating.toFixed(1)}-${meta.event.maxRating.toFixed(1)}`
      : undefined
  const signupProgress = meta.event
    ? `${meta.event.numSignups}/${meta.event.drawSize ?? '-'}`
    : undefined
  // Event slots display the event's signup fee, not the underlying court
  // price (`meta.price`) — the two can differ (e.g. free events on paid courts).
  // Booked/reserved slots show no price: a merged block only carries the first
  // slot's meta, so its price wouldn't reflect what was actually paid (#52).
  const displayPrice = meta.event
    ? meta.event.fee
    : meta.status === SlotStatus.Booked || meta.status === SlotStatus.Reserved
      ? undefined
      : meta.price

  const content = (
    <div style={{ width: '100%' }}>
      {meta.status === SlotStatus.Booked && (
        <div className={styles.cellCoaching}>订场</div>
      )}
      {displayPrice != null && <div className={styles.cellPrice}>¥{displayPrice}</div>}
      {reasonLine && (
        <div
          className={
            meta.status === SlotStatus.Reserved && !meta.event
              ? styles.cellReasonMultiline
              : styles.cellReason
          }
        >
          {reasonLine}
        </div>
      )}
      {ratingRange && <div className={styles.cellEventInfo}>{ratingRange}</div>}
      {signupProgress && <div className={styles.cellEventInfo}>{signupProgress}</div>}
    </div>
  )

  const tooltipText = gameFormatLabel ?? meta.reason
  if (tooltipText) {
    return (
      <Tooltip
        title={tooltipText}
        arrow
        placement="top"
        slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
      >
        {content}
      </Tooltip>
    )
  }
  return content
}

/**
 * Green (as a booked slot) only once every attendee has confirmed; until then
 * the slot is styled as a plain reservation. An appointment with no attendees
 * yet (ENROLL before the first enrollment) is not confirmed.
 * For a viewing coach, green instead marks the coach's own appointments.
 */
function coachingStatusClass(coaching: SlotCoachingMeta, currentCoachId?: string) {
  if (currentCoachId != null) {
    return coaching.coachId === currentCoachId
      ? styles.statusBooked
      : styles.statusReserved
  }
  const allConfirmed =
    coaching.attendees.length > 0 &&
    coaching.attendees.every((a) => a.status === 'CONFIRMED')
  return allConfirmed ? styles.statusBooked : styles.statusReserved
}

/**
 * A slot reserved for a coaching appointment: "课程", then the student's
 * name for a 1:1 course, or confirmed/max for a group course.
 * When all attendees are confirmed, the second line uses a contrasting color
 * (matching the booking slot style).
 */
function CoachingCellContent({ coaching }: { coaching: SlotCoachingMeta }) {
  const confirmed = coaching.attendees.filter((a) => a.status === 'CONFIRMED')
  const allConfirmed =
    coaching.attendees.length > 0 &&
    coaching.attendees.every((a) => a.status === 'CONFIRMED')
  const detail =
    coaching.seats === 1
      ? (coaching.attendees[0]?.name ?? '')
      : `${confirmed.length}/${coaching.seats}`
  const tooltip = coaching.attendees
    .map((a) => `${a.name ?? '未命名'}${a.status === 'CONFIRMED' ? '' : '（待确认）'}`)
    .join('\n')

  const content = (
    <div style={{ width: '100%' }}>
      <div className={styles.cellCoaching}>课程</div>
      {detail && (
        <div className={allConfirmed ? styles.cellReason : styles.cellCoaching}>
          {detail}
        </div>
      )}
    </div>
  )
  if (!tooltip) return content
  return (
    <Tooltip
      title={tooltip}
      arrow
      placement="top"
      slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line' } } }}
    >
      {content}
    </Tooltip>
  )
}
